<script setup lang="ts">
/* ============================================================
   HandoffModal — general comments + explicit feedback copy

   Left: a general-comments editor (auto-saved into the tab).
   Right: the assembled review payload preview.

   Copying feedback never uploads or synchronizes the review. A secondary
   portable download remains available as an explicit local export.
   ============================================================ */

import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import type { Tab } from '@/types'
import { assemblePayload } from '@/composables/usePayload'
import { usePersistence } from '@/composables/usePersistence'
import { pushToast } from '@/composables/useToasts'
import { buildShareUrl, getPlanOrigin, buildPortableHtml, downloadTextFile } from '@/composables/useShare'
import { recordMetric } from '@/composables/useAnalytics'

const props = defineProps<{ tab: Tab }>()
const emit = defineEmits<{ (e: 'close'): void }>()

const { saveTabSoon } = usePersistence()

const notes = ref(props.tab.generalNotes || '')
const copyLabel = ref('Copy feedback')
const notesEl = ref<HTMLTextAreaElement | null>(null)
const panelEl = ref<HTMLElement | null>(null)
// Focus management: keep Tab inside the dialog, and restore focus to whatever
// opened it (the Send feedback button) on close.
let prevFocus: HTMLElement | null = null

function trapTab(e: KeyboardEvent): void {
  if (e.key !== 'Tab' || !panelEl.value) return
  const f = Array.from(
    panelEl.value.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea, input, [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((el) => el.offsetParent !== null)
  if (f.length === 0) return
  const first = f[0]
  const last = f[f.length - 1]
  const active = document.activeElement
  if (e.shiftKey && active === first) {
    e.preventDefault()
    last.focus()
  } else if (!e.shiftKey && active === last) {
    e.preventDefault()
    first.focus()
  }
}

const downloading = ref(false)
// The plan link is context for the agent when this document originated from a
// Redline share. Computing it is local-only and never publishes a local file.
const sourceUrl = ref<string | null>(null)

async function refreshSourceUrl(): Promise<void> {
  const origin = getPlanOrigin(props.tab.hash)
  sourceUrl.value = origin ? await buildShareUrl(origin) : null
}

// Re-seed when the active tab changes underneath the modal.
watch(
  () => props.tab.hash,
  () => {
    notes.value = props.tab.generalNotes || ''
    void refreshSourceUrl()
  },
)

const previewText = computed(() => assemblePayload(props.tab, sourceUrl.value ?? undefined) || '')
const isEmpty = computed(() => !previewText.value.trim())
const displayText = computed(() =>
  isEmpty.value ? 'No content yet. Add notes or inline comments.' : previewText.value,
)
const countText = computed(() => {
  if (isEmpty.value) return ''
  const lines = previewText.value.split('\n').length
  const chars = previewText.value.length
  return `${lines} line${lines === 1 ? '' : 's'} · ${chars} char${chars === 1 ? '' : 's'}`
})

function onNotesInput(): void {
  props.tab.generalNotes = notes.value
  props.tab.updatedAt = Date.now()
  saveTabSoon(props.tab)
}

async function copy(): Promise<void> {
  const text = previewText.value
  if (!text.trim()) {
    pushToast('Nothing to copy')
    return
  }
  try {
    await navigator.clipboard.writeText(text)
    recordMetric('feedback_copied')
    copyLabel.value = 'Copied!'
    pushToast('Review copied — paste it back to your agent.')
    setTimeout(() => {
      copyLabel.value = 'Copy feedback'
    }, 1500)
  } catch {
    pushToast('Copy failed', 'error')
  }
}

/** Download a portable, fully-offline HTML snapshot of the plan + comments. */
async function download(): Promise<void> {
  if (downloading.value) return
  downloading.value = true
  try {
    const { html, filename } = await buildPortableHtml(props.tab)
    downloadTextFile(html, filename)
    recordMetric('portable_downloaded')
    pushToast('Portable HTML downloaded')
  } catch (e) {
    pushToast('Download failed: ' + (e as Error).message, 'error')
  } finally {
    downloading.value = false
  }
}

onMounted(() => {
  prevFocus = document.activeElement as HTMLElement | null
  document.addEventListener('keydown', trapTab, true)
  void refreshSourceUrl()
  nextTick(() => notesEl.value?.focus())
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', trapTab, true)
  prevFocus?.focus?.()
})
</script>

<template>
  <div class="handoff" aria-hidden="false">
    <div class="handoff-backdrop" @click="emit('close')"></div>
    <div ref="panelEl" class="handoff-panel" role="dialog" aria-modal="true" aria-labelledby="handoff-title">
      <header class="handoff-header">
        <div class="handoff-title-group">
          <h2 id="handoff-title" class="handoff-title">Send feedback</h2>
          <span class="handoff-subtitle">{{ tab.filename }}</span>
        </div>
        <button
          class="btn btn-ghost btn-icon-lg handoff-close"
          aria-label="Close"
          @click="emit('close')"
        >
          ×
        </button>
      </header>

      <div class="handoff-body">
        <section class="handoff-edit">
          <label class="handoff-field handoff-field-grow">
            <span class="handoff-section-head">
              <span class="handoff-section-label">General comments</span>
              <span class="handoff-section-meta">overall thoughts</span>
            </span>
            <textarea
              ref="notesEl"
              v-model="notes"
              spellcheck="false"
              placeholder="Freeform notes about the document as a whole…"
              @input="onNotesInput"
            ></textarea>
          </label>
        </section>
        <section class="handoff-preview">
          <div class="handoff-preview-header">
            <span class="handoff-section-label">Review payload</span>
            <span class="handoff-section-meta">{{ countText }}</span>
          </div>
          <pre class="handoff-preview-body" :class="{ 'is-empty': isEmpty }">{{ displayText }}</pre>
        </section>
      </div>

      <footer class="handoff-footer">
        <p class="handoff-hint">
          Paste this into your agent’s chat. It can revise the plan, reply to each thread, and return a new encrypted link.
        </p>
        <div class="handoff-modes">
          <button
            class="btn btn-default btn-md"
            :disabled="downloading"
            title="Download a single offline HTML file of the plan + comments"
            @click="download"
          >
            {{ downloading ? 'Preparing…' : 'Download Plan With Comments' }}
          </button>
          <button
            class="btn btn-primary btn-md"
            title="Copy the structured comments to send back to your agent"
            @click="copy"
          >
            {{ copyLabel }}
          </button>
        </div>
      </footer>
    </div>
  </div>
</template>
